/**
 * 65.15 — grenades: HE and smoke, thrown, on the action clock (constants/action.ts GRENADES).
 *
 * A throw leaves the hand (GRENADES.handPeople person heights up) along a world direction at the speed that makes a 45°
 * throw carry GRENADES.rangePeople on flat ground under the local felt gravity — so the same arm throws as far on any
 * world. It flies under that gravity, bounces off the ground (the person's fine ground, personGroundHit), slides and
 * comes to rest; when its fuse runs out an HE grenade bursts (Match resolves the burst through rocketBlast, the
 * person-family blast) and a smoke grenade becomes a cloud — a sphere that `personLineOfSight` treats as opaque for
 * everyone, people and bots alike, for its lifetime. Pure and deterministic (Math.sqrt only).
 */
import { explosionBlast } from './explosions';
import type { GameState, GrenadeKind, GrenadeState, PlayerState, SmokeCloud, Vec3 } from '../types';
import { windAt, actionWindAccel } from './wind';
import { WEATHER_EFFECTS } from './weather';
import { SMOKE_WIND_DRIFT, GRENADES, GRENADE_AIM, PERSON_BODY_HEIGHT, PHYS_DT, G, ACTION_WIND, REAL_TIMING } from '../constants';
import { vecAdd, vecDot, vecLength, vecNormalize, vecScale, vecSub } from '../math';
import { gravityAccel } from './physics';
import { datan2, dcos, dsin } from './dmath';
import { personSetOf } from './scaleProfile';
import { personGroundHit, personEyePoint } from './fineTerrain';

/** The felt gravity at `p` (what a person-scale throw falls under: the planets' pull × the profile's felt-gravity scale). */
export function feltGravity(state: Pick<GameState, 'planets' | 'config'>, p: Vec3): Vec3 {
  return vecScale(gravityAccel(p, state.planets), personSetOf(state).gravityScale);
}

/** A throw's speed at `p`: a 45° throw carries GRENADES.rangePeople person heights on flat ground (v² = g · range). */
export function throwSpeed(state: Pick<GameState, 'planets' | 'config'>, p: Vec3): number {
  const g = vecLength(feltGravity(state, p));
  const range = GRENADES.rangePeople * PERSON_BODY_HEIGHT * personSetOf(state).k;
  return Math.sqrt(Math.max(1e-12, g * range));
}

/** The burst's numbers in world units (the person-family blast at this scale; core/weapons.ts converts the rocket's the same way). */
export function grenadeBlast(state: Pick<GameState, 'config'>): { radius: number; damage: number } {
  return explosionBlast(state, GRENADES.he.explosion)!;   // 65.35: its class's
}

export type ThrowCheck = { ok: true } | { ok: false; reason: string; code: string };

/** May `p` throw a `kind` grenade at `tick`? */
export function canThrow(p: PlayerState, kind: GrenadeKind, tick: number): ThrowCheck {
  if (!p.alive || p.airborne) return { ok: false, reason: 'Cannot throw now', code: 'not grounded' };
  if (p.mount) return { ok: false, reason: 'Dismount first', code: 'mounted' };
  if (!((p.grenades?.[kind] ?? 0) > 0)) return { ok: false, reason: `No ${kind === 'he' ? 'grenades' : 'smoke'} left`, code: 'none' };
  if ((p.throwReady ?? 0) > tick) return { ok: false, reason: 'Still throwing', code: 'throwing' };
  return { ok: true };
}

/** Throw: the grenade leaves the hand along `dir`; one is spent; the next throw waits GRENADES.throwSeconds. */
export function throwGrenade(state: GameState, thrower: PlayerState, kind: GrenadeKind, dir: Vec3, tick: number): GameState {
  const left = { ...(thrower.grenades ?? { he: 0, smoke: 0 }) };
  left[kind] = Math.max(0, left[kind] - 1);
  const spent = { ...state, players: state.players.map(p => p.id === thrower.id ? { ...p, grenades: left, throwReady: tick + Math.round(GRENADES.throwSeconds / PHYS_DT) } : p) };
  return releaseGrenade(spent, thrower, kind, dir);
}

/**
 * 216 (GameConfig.realTiming): a throw wound up — the grenade spent and the next throw's wait begun now, as `throwGrenade`
 * (so the drawn throw starts this tick), but the grenade leaves the hand REAL_TIMING.throwWindSeconds on (`stepThrowWinds`).
 */
export function windGrenade(state: GameState, thrower: PlayerState, kind: GrenadeKind, dir: Vec3, tick: number): GameState {
  const left = { ...(thrower.grenades ?? { he: 0, smoke: 0 }) };
  left[kind] = Math.max(0, left[kind] - 1);
  const at = tick + Math.max(1, Math.round(REAL_TIMING.throwWindSeconds / PHYS_DT));
  return { ...state, players: state.players.map(p => p.id === thrower.id ? { ...p, grenades: left, throwReady: tick + Math.round(GRENADES.throwSeconds / PHYS_DT), throwWind: { kind, dir: vecNormalize(dir), at } } : p) };
}

/**
 * 216: real timing's step — the wound throws due at `tick` leave the hand (from where the thrower now is); one whose thrower
 * died, took a seat or left the ground first is given back (the grenade not thrown); a fallen unit's stance change ends (it
 * comes back standing). Run before the grenades fly. Nothing without either under way.
 */
export function stepRealTiming(state: GameState, tick: number): GameState {
  if (!state.players.some(p => p.throwWind || (!p.alive && p.stanceShift))) return state;
  let s = state;
  for (const p of state.players) {
    if (!p.alive && p.stanceShift) {
      const { stanceShift: _gone, ...rest } = p;
      s = { ...s, players: s.players.map(q => (q.id === p.id ? rest : q)) };
    }
  }
  for (const p0 of state.players) {
    const p = s.players.find(q => q.id === p0.id)!;
    const w = p.throwWind;
    if (!w || (w.at > tick && p.alive && !p.mount && !p.airborne)) continue;
    const { throwWind: _done, ...rest } = p;
    const release = w.at <= tick && p.alive && !p.mount && !p.airborne;
    const back = release ? rest : { ...rest, grenades: { ...(p.grenades ?? { he: 0, smoke: 0 }), [w.kind]: (p.grenades?.[w.kind] ?? 0) + 1 }, throwReady: tick };
    s = { ...s, players: s.players.map(q => (q.id === p.id ? back : q)) };
    if (release) s = releaseGrenade(s, back, w.kind, w.dir);
  }
  return s;
}

/** The grenade leaves `thrower`'s hand along `dir` (nothing spent here). */
function releaseGrenade(state: GameState, thrower: PlayerState, kind: GrenadeKind, dir: Vec3): GameState {
  const ps = personSetOf(state);
  const hand = personEyePoint(thrower, state.planets, ps, GRENADES.handPeople * PERSON_BODY_HEIGHT * ps.k);
  const d = vecNormalize(dir);
  const grenades = state.grenades ?? [];
  const g: GrenadeState = {
    id: grenades.reduce((m, x) => Math.max(m, x.id + 1), state.grenadeSeq ?? 0),
    ownerId: thrower.id, teamIndex: thrower.teamIndex, kind, planetIndex: thrower.planetIndex,
    pos: hand, vel: vecScale(d, throwSpeed(state, hand)), fuseLeft: GRENADES[kind].fuseSeconds,
  };
  return { ...state, grenadeSeq: g.id + 1, grenades: [...grenades, g] };
}

/** What a tick of grenades produced: HE bursts (for Match to resolve), clouds made. */
export interface GrenadeStep { state: GameState; bursts: Array<{ grenade: GrenadeState; point: Vec3 }>; clouds: SmokeCloud[] }

/** One tick of grenades and clouds: fly and bounce, run the fuses down, burst or smoke; clouds age and clear. */
/** 62.47: where a smoke cloud has drifted after `dt`: the wind at it (core/wind.ts windAt), read as a speed along the ground. */
function smokeDrift(state: GameState, c: SmokeCloud, seconds: number, dt: number, person: number): Vec3 {
  const planet = state.planets[c.planetIndex];
  if (!planet) return c.pos;
  const rel = vecSub(c.pos, planet.position);
  const w = windAt(state, c.planetIndex, rel, seconds);
  if (!w) return c.pos;
  const dist = vecLength(rel);
  if (dist < 1e-9) return c.pos;
  const n = vecScale(rel, 1 / dist);
  const tangent = vecSub(w, vecScale(n, vecDot(w, n)));   // along the ground, as the kernel takes the wind on a shell
  const storm = WEATHER_EFFECTS.storm.wind * G * planet.mass / (planet.radius * planet.radius);
  const moved = vecAdd(rel, vecScale(tangent, (SMOKE_WIND_DRIFT * person * dt) / storm));
  return vecAdd(planet.position, vecScale(vecNormalize(moved), dist));   // the same height above the centre
}

/** One tick of a grenade's flight: gravity (and the wind, with the switch), the ground's bounce and slide, at rest when spent. */
function flyGrenade(state: GameState, g: GrenadeState, dt: number, person: number): GrenadeState {
  // 62.52: a grenade in flight drifts with the wind where it is (decision 120's switch; calm without it)
  const wind = state.config.wind ? actionWindAccel(state, g.planetIndex, g.pos, ACTION_WIND.grenade) : null;
  const vel = vecAdd(g.vel, vecScale(wind ? vecAdd(feltGravity(state, g.pos), wind) : feltGravity(state, g.pos), dt));
  let pos = vecAdd(g.pos, vecScale(vel, dt));
  let v = vel;
  let resting: true | undefined;
  const hit = personGroundHit(pos, state);
  if (hit) {
    // bounce: the speed into the ground reversed and cut, the speed along it slowed; slow enough, it lies still
    const planet = state.planets[hit.planetIndex];
    const n = vecNormalize(vecSub(hit.point, planet.position));
    const into = vecDot(v, n);
    const along = vecScale(vecSub(v, vecScale(n, into)), GRENADES.friction);
    const out = into < 0 ? -into * GRENADES.restitution : into;
    v = vecAdd(along, vecScale(n, out));
    pos = vecAdd(hit.point, vecScale(n, 1e-4 * person));
    // at rest: barely sliding, and bouncing less than one tick of gravity would pull it back (it would land again next tick)
    if (vecLength(along) < 0.5 * person && out <= vecLength(feltGravity(state, pos)) * dt) { v = { x: 0, y: 0, z: 0 }; resting = true; }
  }
  return { ...g, pos, vel: v, ...(resting ? { resting } : {}) };
}

/**
 * Where a `kind` grenade thrown from `from` along `dir` (on `planetIndex`) bursts — HE — or makes its cloud — smoke: its
 * flight run tick by tick to its fuse with the flight's own step (the wind, the ground, the bounces), as `stepGrenades`
 * would fly it. A bot's estimate (aimThrow); nothing in the match changes.
 */
export function predictBurst(state: GameState, from: Vec3, dir: Vec3, kind: GrenadeKind, planetIndex: number): Vec3 {
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k;
  let g: GrenadeState = { id: -1, ownerId: -1, teamIndex: -1, kind, planetIndex, pos: from, vel: vecScale(vecNormalize(dir), throwSpeed(state, from)), fuseLeft: GRENADES[kind].fuseSeconds };
  for (let fuse = g.fuseLeft; ; ) {
    if (!g.resting) g = flyGrenade(state, g, PHYS_DT, person);
    fuse -= PHYS_DT;
    if (fuse <= 0) return g.pos;
  }
}

export function stepGrenades(state: GameState, dt: number): GrenadeStep {
  const bursts: GrenadeStep['bursts'] = [];
  const clouds: SmokeCloud[] = [];
  const ps = personSetOf(state);
  const person = PERSON_BODY_HEIGHT * ps.k;
  let seq = state.smokeSeq ?? 0;
  const flying: GrenadeState[] = [];
  for (const g0 of state.grenades ?? []) {
    let g = g0;
    if (!g.resting) g = flyGrenade(state, g, dt, person);
    const fuseLeft = g.fuseLeft - dt;
    if (fuseLeft > 0) { flying.push({ ...g, fuseLeft }); continue; }
    if (g.kind === 'he') bursts.push({ grenade: g, point: g.pos });
    else clouds.push({ id: seq++, teamIndex: g.teamIndex, planetIndex: g.planetIndex, pos: g.pos, radius: GRENADES.smoke.cloudPeople * person, left: GRENADES.smoke.cloudSeconds });
  }
  // 62.47: a cloud drifts with the wind where it stands (decision 120's switch; calm without it): along the ground, at
  // SMOKE_WIND_DRIFT people a second for a storm's wind in the open, keeping its height
  const seconds = (state.tick ?? 0) * PHYS_DT;
  const standing = (state.smokes ?? []).map(c => ({ ...c, pos: state.config.wind ? smokeDrift(state, c, seconds, dt, person) : c.pos, left: c.left - dt })).filter(c => c.left > 0);
  const smokes = [...standing, ...clouds];
  return {
    state: {
      ...state,
      grenades: flying.length ? flying : undefined,
      smokes: smokes.length ? smokes : undefined,
      ...(clouds.length ? { smokeSeq: seq } : {}),
    },
    bursts, clouds,
  };
}

export { smokeBlocks } from './smoke';

/**
 * The throw direction from `from` that lands on `to`: the low arc at the throw's speed with the target's height
 * (tan θ = (v² − √(v⁴ − g(g x² + 2 y v²))) / (g x), x along the ground, y up), or 45° when it is out of reach.
 * Along the ground toward the target, lifted by θ from the local horizontal.
 */
/**
 * The bots' throw (the coordinator, after lane E's 298 finding: "a thrown grenade should burst at its target"): the throw
 * whose `kind` grenade bursts — or clouds — nearest `to`. `throwDirTo` lands the grenade there, and then it carries on: at
 * the same speed whatever the range, the low arc comes down flat and slides, and an HE grenade thrown at a generator 10–12
 * people off burst 6–9 people past it, out of its 2.5-person blast (lab-7 ½). Aimed short by the carry instead: the burst
 * predicted with the flight's own step (predictBurst), the aim point moved back along the ground by the miss, and again,
 * up to GRENADE_AIM.tries times or within GRENADE_AIM.withinPeople; the throw that bursts nearest is the one thrown.
 * (No high arc: at the throw's speed a lob to 10 people flies ~4 s, past the HE fuse's 3 s — it would burst in the air.)
 */
export function aimThrow(state: GameState, from: Vec3, to: Vec3, up: Vec3, kind: GrenadeKind, planetIndex: number): Vec3 {
  const n = vecNormalize(up);
  const off = vecSub(to, from);
  const flat = vecSub(off, vecScale(n, vecDot(off, n)));
  const d = vecLength(flat);
  const first = throwDirTo(state, from, to, up);
  if (!(d > 0)) return first;
  const h = vecScale(flat, 1 / d);
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k;
  let dir = first, best = first, bestMiss = Infinity, aim = d;
  for (let i = 0; i < GRENADE_AIM.tries; i++) {
    const at = predictBurst(state, from, dir, kind, planetIndex);
    const miss = vecLength(vecSub(at, to));
    if (miss < bestMiss) { bestMiss = miss; best = dir; }
    if (miss <= GRENADE_AIM.withinPeople * person) break;
    // the miss along the throw's line, taken off the aim point (never behind the hand)
    const along = vecDot(vecSub(at, from), h) - d;
    aim = Math.max(GRENADE_AIM.minPeople * person, aim - along);
    dir = throwDirTo(state, from, vecAdd(vecSub(to, vecScale(h, d)), vecScale(h, aim)), up);
  }
  return best;
}

export function throwDirTo(state: Pick<GameState, 'planets' | 'config'>, from: Vec3, to: Vec3, up: Vec3): Vec3 {
  const n = vecNormalize(up);
  const off = vecSub(to, from);
  const flat = vecSub(off, vecScale(n, vecDot(off, n)));
  const d = vecLength(flat);
  if (!(d > 0)) return n;   // straight overhead: straight up
  const h = vecScale(flat, 1 / d);
  const v = throwSpeed(state, from);
  const g = vecLength(feltGravity(state, from));
  const y = vecDot(off, n);
  const v2 = v * v;
  const disc = v2 * v2 - g * (g * d * d + 2 * y * v2);
  const theta = disc < 0 ? Math.PI / 4 : datan2(v2 - Math.sqrt(disc), g * d);
  return vecNormalize(vecAdd(vecScale(h, dcos(theta)), vecScale(n, dsin(theta))));
}
